import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { RunTimeConfigService } from '@shared/services/run-time-config.service';
import { ToastService } from '@shared/modules/toast/services/toast.service';

export interface HttpParamsOptions {
  /**
   * String representation of the HTTP parameters in URL-query-string format.
   * Mutually exclusive with `fromObject`.
   */
  fromString?: string;
  /** Object map of the HTTP parameters. Mutually exclusive with `fromString`. */
  fromObject?: {
    [param: string]: string | ReadonlyArray<string>;
  };
  /** Encoding codec used to parse and serialize the parameters. */
  encoder?: HttpParameterCodec;
}

export class CustomHttpParams extends HttpParams {
  constructor(public customParams: any, public params: any) {
    super({ fromObject: params } as HttpParamsOptions);
  }
}

interface PathParam {
  param: string;
  replaceWith: string;
}

interface HttpOptions {
  body?: any;
  headers?: any;
  reportProgress?: boolean;
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  responseType?: 'json';
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly baseURLConfig: any;
  constructor(
    private http: HttpClient,
    private runTimeConfigService: RunTimeConfigService,
    private toastService: ToastService
  ) {
    this.baseURLConfig = this.runTimeConfigService.getConfig('baseURLConfig');
  }

  get(originalEndPointObject: any, options?: HttpOptions, urlPathParams?: PathParam[]) {
    let baseURL = '';
    let endPointObject = {};

    if (typeof originalEndPointObject === 'object') {
      endPointObject = JSON.parse(JSON.stringify(originalEndPointObject));
      // const headers = new HttpHeaders(endPointObject.httpHeaders);
      options = this.parseOptions(options, endPointObject);

      if (endPointObject['baseApp']) {
        if (this.baseURLConfig[endPointObject['baseApp']] && this.baseURLConfig[endPointObject['baseApp']].baseURL) {
          baseURL = this.baseURLConfig[endPointObject['baseApp']].baseURL;
        } else {
          this.showBaseURLError(endPointObject['baseApp']);
          throw new Error('Incorrect Base URL configuration in the application!');
        }
      }
      // Replace any path params with value
      endPointObject = this.parseURLParams(endPointObject, urlPathParams);
    } else {
      endPointObject['endPoint'] = originalEndPointObject;
    }

    // console.info(environment[endPointObject["baseApp"]] + endPointObject['endPoint'], '[APIService: GET Request]');
    return this.http.get(baseURL + endPointObject['endPoint'], options);
  }

  post(originalEndPointObject: any, payload: any, options?: HttpOptions, urlPathParams?: PathParam[]) {
    let baseURL = '';
    let endPointObject = {};

    if (typeof originalEndPointObject === 'object') {
      endPointObject = JSON.parse(JSON.stringify(originalEndPointObject));
      // const headers = new HttpHeaders(endPointObject.httpHeaders);
      options = this.parseOptions(options, endPointObject);

      if (endPointObject['baseApp']) {
        if (this.baseURLConfig[endPointObject['baseApp']] && this.baseURLConfig[endPointObject['baseApp']].baseURL) {
          baseURL = this.baseURLConfig[endPointObject['baseApp']].baseURL;
        } else {
          this.showBaseURLError(endPointObject['baseApp']);
          throw new Error('Incorrect Base URL configuration in the application!');
        }
      }
      endPointObject = this.parseURLParams(endPointObject, urlPathParams);
    } else {
      endPointObject['endPoint'] = originalEndPointObject;
    }

    // console.info(environment[endPointObject["baseApp"]] + endPointObject['endPoint'], '[APIService: POST Request]');
    return this.http.post(baseURL + endPointObject['endPoint'], payload, options);
  }

  put(originalEndPointObject: any, payload: any, options?: HttpOptions, urlPathParams?: PathParam[]) {
    let endPointObject = JSON.parse(JSON.stringify(originalEndPointObject));
    let baseURL = '';

    // const headers = new HttpHeaders(endPointObject.httpHeaders);
    options = this.parseOptions(options, endPointObject);

    if (endPointObject['baseApp']) {
      if (this.baseURLConfig[endPointObject['baseApp']] && this.baseURLConfig[endPointObject['baseApp']].baseURL) {
        baseURL = this.baseURLConfig[endPointObject['baseApp']].baseURL;
      } else {
        this.showBaseURLError(endPointObject['baseApp']);
        throw new Error('Incorrect Base URL configuration in the application!');
      }
    }
    endPointObject = this.parseURLParams(endPointObject, urlPathParams);

    // console.info(environment[endPointObject["baseApp"]] + endPointObject['endPoint'], '[APIService: POST Request]');
    return this.http.put(baseURL + endPointObject['endPoint'], payload, options);
  }

  delete(originalEndPointObject: any, options?: HttpOptions, urlPathParams?: PathParam[]) {
    let endPointObject = JSON.parse(JSON.stringify(originalEndPointObject));
    let baseURL = '';

    // const headers = new HttpHeaders(endPointObject.httpHeaders);
    options = this.parseOptions(options, endPointObject);

    if (endPointObject['baseApp']) {
      if (this.baseURLConfig[endPointObject['baseApp']] && this.baseURLConfig[endPointObject['baseApp']].baseURL) {
        baseURL = this.baseURLConfig[endPointObject['baseApp']].baseURL;
      } else {
        this.showBaseURLError(endPointObject['baseApp']);
        throw new Error('Incorrect Base URL configuration in the application!');
      }
    }
    endPointObject = this.parseURLParams(endPointObject, urlPathParams);

    // console.info(environment[endPointObject["baseApp"]] + endPointObject['endPoint'], '[APIService: POST Request]');
    return this.http.request('delete', baseURL + endPointObject['endPoint'], options);
  }

  // postNormal(endPointURL: string, payload: any, options?: HttpOptions, urlPathParams?: PathParam[]) {
  //   const endPointObject = {
  //     baseApp: '',
  //     endPoint: endPointURL,
  //     skipInterceptorErrorForCodes: [],
  //     'httpHeaders.env.production': {},
  //     'httpHeaders.env.staging': {},
  //     cachingOptions: {
  //       cachingEnabled: false,
  //       cachingDuration: 10000
  //     }
  //   };
  //   let baseURL = '';
  //
  //   // const headers = new HttpHeaders(endPointObject.httpHeaders);
  //   options = this.parseOptions(options, endPointObject);
  //
  //   if (endPointObject["baseApp"]) {
  //     baseURL = environment[endPointObject["baseApp"]].baseURL;
  //   }
  //   endPointObject = this.parseURLParams(endPointObject, urlPathParams);
  //
  //   // console.info(environment[endPointObject["baseApp"]] + endPointObject['endPoint'], '[APIService: POST Request]');
  //   return this.http.post(baseURL + endPointObject['endPoint'], payload, options);
  // }

  patch(originalEndPointObject: any, payload: any, options?: HttpOptions, urlPathParams?: PathParam[]) {
    let endPointObject = JSON.parse(JSON.stringify(originalEndPointObject));
    let baseURL = '';

    // const headers = new HttpHeaders(endPointObject.httpHeaders);
    options = this.parseOptions(options, endPointObject);

    if (endPointObject['baseApp']) {
      if (this.baseURLConfig[endPointObject['baseApp']] && this.baseURLConfig[endPointObject['baseApp']].baseURL) {
        baseURL = this.baseURLConfig[endPointObject['baseApp']].baseURL;
      } else {
        this.showBaseURLError(endPointObject['baseApp']);
        throw new Error('Incorrect Base URL configuration in the application!');
      }
    }
    endPointObject = this.parseURLParams(endPointObject, urlPathParams);

    // console.info(environment[endPointObject["baseApp"]] + endPointObject['endPoint'], '[APIService: POST Request]');
    return this.http.patch(baseURL + endPointObject['endPoint'], payload, options);
  }

  private showBaseURLError(baseApp: string) {
    this.toastService.showToast(
      'BaseURL is not correctly configured for ' + baseApp + '. Please check deployment configuration!',
      'toastError',
      {
        clearToasts: true
      }
    );
  }

  private parseURLParams(endPointObject: any, urlPathParams: PathParam[]) {
    // if param is defined in api config but no url path params are passed, clean URL to be without params
    if (endPointObject['endPoint'].indexOf('/:') > -1 && !urlPathParams) {
      endPointObject['endPoint'] = endPointObject['endPoint'].split('/:')[0];
    } else if (urlPathParams) {
      urlPathParams = JSON.parse(JSON.stringify(urlPathParams));
      urlPathParams.forEach(pathParam => {
        endPointObject['endPoint'] = endPointObject['endPoint'].replace(':' + pathParam.param, pathParam.replaceWith);
      });
    }
    return endPointObject;
  }

  private parseOptions(options: HttpOptions, endPointObject: any) {
    let headers = new HttpHeaders();
    // const headersKey = this.getEnvironmentKey(endPointObject);

    if (options) {
      // existing options were found. Maintain them and add options from API configs
      if (options.params) {
        options.params = new CustomHttpParams(endPointObject, options.params);
      } else {
        options.params = new CustomHttpParams(endPointObject, {});
      }

      // if (options.headers) {
      //   if (headersKey && Object.keys(endPointObject[headersKey]).length > 0) {
      //     Object.keys(endPointObject[headersKey]).forEach(header => {
      //       options.headers = options.headers.append(header, endPointObject[headersKey][header]);
      //     });
      //   }
      // } else {
      //   if (headersKey && Object.keys(endPointObject[headersKey]).length > 0) {
      //     Object.keys(endPointObject[headersKey]).forEach(header => {
      //       headers = headers.append(header, endPointObject[headersKey][header]);
      //     });
      //     options.headers = headers;
      //   }
      // }
    } else {
      // no options were passed, add options as per API configs
      options = {
        params: new CustomHttpParams(endPointObject, {})
      };

      // if (headersKey && Object.keys(endPointObject[headersKey]).length > 0) {
      //   Object.keys(endPointObject[headersKey]).forEach(header => {
      //     headers = headers.append(header, endPointObject[headersKey][header]);
      //   });
      //   options.headers = headers;
      // }
    }

    // Check and add token if available in config. If existing app config - Authorization header was found, do not take from root config
    let bearerToken;
    if (this.baseURLConfig[endPointObject['baseApp']] && this.baseURLConfig[endPointObject['baseApp']].bearerToken) {
      bearerToken = this.baseURLConfig[endPointObject['baseApp']].bearerToken;
    }

    // if (options.headers) {
    //   console.log(options.headers.keys());
    // }

    if (bearerToken && !headers.get('Authorization')) {
      if (options && options.headers) {
        options.headers = options.headers.append('Authorization', 'Bearer ' + bearerToken);
      } else {
        headers = headers.append('Authorization', 'Bearer ' + bearerToken);
        options.headers = headers;
      }
    }

    return options;
  }
}
